export const WAVE_WIDTH = 1240;
export const WAVE_HEIGHT = 45;
const BAR_WIDTH = 3;
const BAR_GAP: number = 0.5;

export async function generateWaveformData(
  file: File,
): Promise<number[][] | null> {
  const audioContext = new window.AudioContext();
  try {
    const buffer = await readFileAsArrayBuffer(file);
    if (!buffer) return null;

    const decoded = await decodeAudioData(audioContext, buffer);
    if (!decoded) return null;

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) return null;

    canvas.width = WAVE_WIDTH;
    canvas.height = WAVE_HEIGHT;

    return extractBuffer(decoded, context);
  } finally {
    try {
      await audioContext.close();
    } catch {
      // Ignore close failures; waveform generation already completed/failed.
    }
  }
}

function readFileAsArrayBuffer(file: File): Promise<ArrayBuffer | null> {
  return new Promise(resolve => {
    const reader = new FileReader();
    reader.onload = e => {
      const result = e.target?.result;
      resolve(result instanceof ArrayBuffer ? result : null);
    };
    reader.onerror = () => resolve(null);
    reader.onabort = () => resolve(null);
    reader.readAsArrayBuffer(file);
  });
}

function decodeAudioData(
  audioContext: AudioContext,
  buffer: ArrayBuffer,
): Promise<AudioBuffer | null> {
  return new Promise(resolve => {
    try {
      audioContext.decodeAudioData(
        buffer,
        decoded => resolve(decoded),
        () => resolve(null),
      );
    } catch {
      resolve(null);
    }
  });
}

function extractBuffer(buffer: AudioBuffer, context: CanvasRenderingContext2D) {
  const waveData: number[][] = [];
  const channelData = buffer.getChannelData(0);
  const sections = WAVE_WIDTH;
  const len = Math.max(1, Math.floor(channelData.length / sections));
  const maxHeight = WAVE_HEIGHT;
  const vals: number[] = [];
  for (let i = 0; i < sections; i += BAR_WIDTH) {
    vals.push(bufferMeasure(i * len, len, channelData) * 10000);
  }

  const peak = Math.max(...vals, 1);
  for (let j = 0; j < sections; j += BAR_WIDTH) {
    const scale = maxHeight / peak;
    let val = bufferMeasure(j * len, len, channelData) * 10000;
    val *= scale;
    val += 1;
    waveData.push(getBarData(j, val));
  }

  // clear canvas for redrawing
  context.clearRect(0, 0, WAVE_WIDTH, WAVE_HEIGHT);
  return waveData;
}

function bufferMeasure(position: number, length: number, data: Float32Array) {
  if (!data.length || length <= 0) return 0;
  let sum = 0.0;
  const safeStart = Math.max(0, position);
  const safeEnd = Math.min(position + length - 1, data.length - 1);
  if (safeEnd < safeStart) return 0;

  for (let i = safeStart; i <= safeEnd; i++) {
    sum += Math.pow(data[i], 2);
  }
  return Math.sqrt(sum / (safeEnd - safeStart + 1));
}

function getBarData(i: number, h: number) {
  let w = BAR_WIDTH;
  if (BAR_GAP !== 0) {
    w *= Math.abs(1 - BAR_GAP);
  }
  const x = i + w / 2,
    y = WAVE_HEIGHT - h;

  return [x, y, w, h];
}
